import type { SpeakOptions, TTSProvider } from "./types"
import { speak as edgeSpeak } from "./edge-tts"
import { speak as localSpeak } from "./local-tts"

const DEFAULT_TTS_LANG = "zh"
const DEFAULT_TTS_PROVIDER: TTSProvider = "LocalTTS"
const EDGE_TTS_PROVIDER_ENV_VALUE = "edgetts"
const DEFAULT_CHINESE_EDGE_VOICE = "zh-CN-XiaoxiaoNeural"
const noopSpeakResult = { stopSpeak: () => {} }

export const langCode2TTSLang: Record<string, string> = {
  en: "en-US",
  "zh-Hans": "zh-CN",
  "zh-Hant": "zh-TW",
  yue: "zh-HK",
  lzh: "zh-CN",
  ja: "ja-JP",
  ko: "ko-KR",
  fr: "fr-FR",
  de: "de-DE",
  es: "es-ES",
  it: "it-IT",
  ru: "ru-RU",
  pt: "pt-PT",
  nl: "nl-NL",
  pl: "pl-PL",
  ar: "ar-001",
  bg: "bg-BG",
  ca: "ca-ES",
  cs: "cs-CZ",
  da: "da-DK",
  el: "el-GR",
  fi: "fi-FI",
  he: "he-IL",
  hi: "hi-IN",
  hr: "hr-HR",
  id: "id-ID",
  vi: "vi-VN",
  sv: "sv-SE",
}

let supportVoices: SpeechSynthesisVoice[] = []
if (window.speechSynthesis) {
  window.speechSynthesis.onvoiceschanged = () => {
    supportVoices = window.speechSynthesis.getVoices()
  }
}

function normalizeVoiceText(value: string | undefined) {
  return (value ?? "").toLowerCase().replace(/\s+/g, "")
}

function isChineseLang(langTag: string) {
  return ["zh-CN", "zh-TW", "zh-HK"].includes(langTag)
}

function normalizeTextOption(value: string | undefined) {
  return value?.trim() || undefined
}

function clampNumber(value: number | undefined, fallback: number, min: number, max: number) {
  if (!Number.isFinite(value)) return fallback
  return Math.min(Math.max(value as number, min), max)
}

function normalizeProviderEnv(value: string | undefined) {
  return value?.trim().toLowerCase().replace(/[-_\s]/g, "") ?? ""
}

export function isEdgeTTSEnabled() {
  return normalizeProviderEnv(import.meta.env.VITE_TTS_PROVIDER) === EDGE_TTS_PROVIDER_ENV_VALUE
}

function resolveProvider(provider: TTSProvider | undefined): TTSProvider {
  if (provider === "EdgeTTS") {
    return isEdgeTTSEnabled() ? "EdgeTTS" : DEFAULT_TTS_PROVIDER
  }
  if (provider) return provider
  return isEdgeTTSEnabled() ? "EdgeTTS" : DEFAULT_TTS_PROVIDER
}

function isLocalTTSPlaybackInterruption(error: unknown) {
  if (!(error instanceof Error) || error.name !== "AbortError") return false
  const message = error.message.toLowerCase()
  return (
    message.includes("play() request was interrupted") ||
    message.includes("media was removed") ||
    message.includes("bodystreambuffer was aborted")
  )
}

function scoreVoice(voice: SpeechSynthesisVoice, langTag: string, preferredVoiceName?: string) {
  const name = normalizeVoiceText(voice.name)
  const voiceURI = normalizeVoiceText(voice.voiceURI)
  const preferred = normalizeVoiceText(preferredVoiceName)
  let score = 0

  if (voice.lang === langTag) score += 100
  else if (voice.lang?.startsWith(langTag)) score += 80
  else if (langTag === "zh-CN" && voice.lang?.startsWith("zh")) score += 40
  else return -1

  if (preferred && (voiceURI === preferred || name === preferred)) score += 80
  if (preferred && (voiceURI.includes(preferred) || name.includes(preferred))) score += 60
  if (name.includes("xiaoxiao") || voiceURI.includes("xiaoxiao") || name.includes("小晓") || name.includes("晓晓")) score += 50
  if (name.includes("microsoft") || voiceURI.includes("microsoft")) score += 20
  if (name.includes("natural") || voiceURI.includes("natural")) score += 15
  if (voice.default) score += 5

  return score
}

function pickBestVoice(voices: SpeechSynthesisVoice[], langTag: string, preferredVoiceName?: string) {
  return voices
    .map(voice => ({ voice, score: scoreVoice(voice, langTag, preferredVoiceName) }))
    .filter(item => item.score >= 0)
    .sort((a, b) => b.score - a.score)[0]?.voice ?? null
}

function speakWithWebSpeech(
  { text, onFinish }: Pick<SpeakOptions, "text" | "onFinish">,
  langTag: string,
  voiceName: string | undefined,
  rate: number,
  volume: number,
) {
  if (!window.speechSynthesis || typeof SpeechSynthesisUtterance === "undefined") {
    throw new Error("Web Speech is not supported")
  }

  const utterance = new SpeechSynthesisUtterance()
  if (onFinish) {
    utterance.addEventListener("end", onFinish, { once: true })
  }

  utterance.text = text
  utterance.lang = langTag
  utterance.rate = rate
  utterance.volume = volume ? volume / 100 : 1

  const voices = supportVoices.length > 0 ? supportVoices : window.speechSynthesis.getVoices()
  const voice = pickBestVoice(voices, langTag, voiceName)
  if (!voice && isChineseLang(langTag)) {
    throw new Error(`No Web Speech voice is available for ${langTag}`)
  }
  utterance.voice = voice

  window.speechSynthesis.speak(utterance)
  return { stopSpeak: () => window.speechSynthesis.cancel() }
}

export async function speak({ text, lang, provider, voice, model, rate, volume, speed, onFinish }: SpeakOptions) {
  const resolvedProvider = resolveProvider(provider)
  const langTag = langCode2TTSLang[lang ?? DEFAULT_TTS_LANG] ?? "zh-CN"
  const normalizedVoice = normalizeTextOption(voice)
  const normalizedModel = normalizeTextOption(model)
  const voiceCfg = normalizedVoice ?? (isChineseLang(langTag) ? DEFAULT_CHINESE_EDGE_VOICE : undefined)
  const resolvedRate = clampNumber(rate, 1, 0.1, 10)
  const resolvedVolume = clampNumber(volume, 100, 0, 100)
  const webSpeechSpeak = () => speakWithWebSpeech({ text, onFinish }, langTag, voiceCfg, resolvedRate, resolvedVolume)
  const localTTSSpeak = () => localSpeak({
    text,
    lang,
    onFinish,
    provider: "LocalTTS",
    ...(normalizedVoice ? { voice: normalizedVoice } : {}),
    ...(normalizedModel ? { model: normalizedModel } : {}),
    ...(Number.isFinite(speed) ? { speed } : {}),
  })

  if (resolvedProvider === "EdgeTTS") {
    try {
      return await edgeSpeak({ text, lang: langTag, onFinish, voice: voiceCfg, rate: resolvedRate, volume: resolvedVolume })
    } catch (error) {
      console.warn("[tts] EdgeTTS failed, falling back to Web Speech", error)
    }

    try {
      return webSpeechSpeak()
    } catch (error) {
      console.warn("[tts] Web Speech fallback failed, falling back to LocalTTS", error)
      try {
        return await localTTSSpeak()
      } catch (localTTSError) {
        console.warn("[tts] LocalTTS fallback failed", localTTSError)
        return noopSpeakResult
      }
    }
  }

  if (resolvedProvider === "LocalTTS") {
    try {
      return await localTTSSpeak()
    } catch (error) {
      if (isLocalTTSPlaybackInterruption(error)) {
        return noopSpeakResult
      }
      console.warn("[tts] LocalTTS failed, falling back to Web Speech", error)
      try {
        return webSpeechSpeak()
      } catch (webSpeechError) {
        console.warn("[tts] Web Speech fallback failed", webSpeechError)
        return noopSpeakResult
      }
    }
  }

  try {
    return webSpeechSpeak()
  } catch (error) {
    console.warn("[tts] Web Speech failed, falling back to LocalTTS", error)
    try {
      return await localTTSSpeak()
    } catch (localTTSError) {
      console.warn("[tts] LocalTTS fallback failed", localTTSError)
      return noopSpeakResult
    }
  }
}
